/**
 * Utilità per la gestione intelligente dei colori e temi
 */

// Converte HEX in RGB
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

// Converte RGB in HEX
export function rgbToHex(r: number, g: number, b: number): string {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

// Converte RGB in HSL
export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

// Converte HSL in RGB
export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  h /= 360;
  s /= 100;
  l /= 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

// Calcola la luminosità relativa secondo WCAG
export function getRelativeLuminance(r: number, g: number, b: number): number {
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

// Calcola il contrasto tra due colori (WCAG)
export function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 1;

  const l1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

// Determina automaticamente se usare testo bianco o nero
export function getOptimalTextColor(backgroundColor: string): string {
  const whiteContrast = getContrastRatio(backgroundColor, "#FFFFFF");
  const blackContrast = getContrastRatio(backgroundColor, "#1A1A1A");

  // Usa bianco se ha più contrasto, altrimenti nero
  return whiteContrast > blackContrast ? "#FFFFFF" : "#1A1A1A";
}

// Genera un gradiente intelligente per lo sfondo
export function generateBackgroundGradient(baseColor: string): string {
  const rgb = hexToRgb(baseColor);
  if (!rgb) return baseColor;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Se è chiaro, scurisci; se è scuro, schiarisci leggermente
  const lightnessAdjustment = hsl.l > 50 ? -15 : 10;
  const newLightness = Math.max(0, Math.min(100, hsl.l + lightnessAdjustment));

  const adjustedRgb = hslToRgb(hsl.h, hsl.s, newLightness);
  const adjustedColor = rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);

  // Gradiente diagonale
  return `linear-gradient(135deg, ${baseColor} 0%, ${adjustedColor} 100%)`;
}

// Genera un gradiente vibrante per le card
export function generateCardGradient(baseColor: string, opacity: number = 0.85): string {
  const rgb = hexToRgb(baseColor);
  if (!rgb) return `rgba(255,255,255,${opacity})`;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Crea una variante leggermente più satura e con luminosità diversa
  const saturationBoost = Math.min(100, hsl.s + 10);
  const lightnessVariation = hsl.l > 50 ? hsl.l - 5 : hsl.l + 5;

  const variant1Rgb = hslToRgb(hsl.h, saturationBoost, lightnessVariation);
  const variant2Rgb = hslToRgb(hsl.h, hsl.s, hsl.l);

  return `linear-gradient(135deg, rgba(${variant1Rgb.r},${variant1Rgb.g},${variant1Rgb.b},${opacity}) 0%, rgba(${variant2Rgb.r},${variant2Rgb.g},${variant2Rgb.b},${opacity}) 100%)`;
}

// Genera un colore per le icone più vibrante
export function enhanceIconColor(baseColor: string): string {
  const rgb = hexToRgb(baseColor);
  if (!rgb) return baseColor;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Aumenta saturazione per rendere più vibrante
  const enhancedSaturation = Math.min(100, hsl.s + 15);
  // Leggero aumento della luminosità se troppo scuro
  const enhancedLightness = hsl.l < 40 ? hsl.l + 10 : hsl.l;

  const enhancedRgb = hslToRgb(hsl.h, enhancedSaturation, enhancedLightness);
  return rgbToHex(enhancedRgb.r, enhancedRgb.g, enhancedRgb.b);
}

// Tipo per il tema generato
export interface ColorTheme {
  background: {
    solid: string;
    gradient: string;
  };
  card: {
    solid: string;
    gradient: string;
  };
  icon: {
    primary: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
}

// Genera un tema completo basato sui colori scelti dall'utente
export function generateColorTheme(
  backgroundBase: string,
  cardBase: string,
  iconBase: string
): ColorTheme {
  return {
    background: {
      solid: backgroundBase,
      gradient: generateBackgroundGradient(backgroundBase),
    },
    card: {
      solid: cardBase,
      gradient: generateCardGradient(cardBase, 1.0), // 100% opacità
    },
    icon: {
      primary: enhanceIconColor(iconBase),
    },
    text: {
      primary: getOptimalTextColor(cardBase),
      secondary: getOptimalTextColor(cardBase) === "#FFFFFF" 
        ? "rgba(255,255,255,0.8)" 
        : "rgba(26,26,26,0.8)",
    },
  };
}

// Helper per convertire un colore in rgba con alpha
export function hexToRgba(hex: string, alpha: number = 1): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,0,0,${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

// Genera un colore analogo per un gradiente
export function getAnalogousColor(baseColor: string): string {
  const rgb = hexToRgb(baseColor);
  if (!rgb) return baseColor;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Sposta la tonalità di 30 gradi per un colore analogo
  const analogousHue = (hsl.h + 30) % 360;

  const analogousRgb = hslToRgb(analogousHue, hsl.s, hsl.l);
  return rgbToHex(analogousRgb.r, analogousRgb.g, analogousRgb.b);
}

// Preset di temi popolari
export const themePresets = [
  {
    name: "Rosa Elegante",
    background: "#E91E63",
    card: "#F8F8F8",
    icon: "#E91E63",
  },
  {
    name: "Blu Professionale",
    background: "#1E40AF",
    card: "#F0F4F8",
    icon: "#3B82F6",
  },
  {
    name: "Verde Natura",
    background: "#10B981",
    card: "#FFFFFF",
    icon: "#059669",
  },
  {
    name: "Viola Creativo",
    background: "#8B5CF6",
    card: "#FAF5FF",
    icon: "#7C3AED",
  },
  {
    name: "Arancio Energia",
    background: "#F97316",
    card: "#FFF7ED",
    icon: "#EA580C",
  },
  {
    name: "Grigio Minimalista",
    background: "#374151",
    card: "#F9FAFB",
    icon: "#6B7280",
  },
];

